import { modals } from '@mantine/modals'
import showModalNotification from '@v2/commons/modals/ModalNotification/showModalNotification'
import { handleMutationError } from '@v2/commons/utils/errorHandling/handleMutationError'
import { useRouter } from '@v2/commons/utils/navigation'
import { DEFAULT_ROUTES } from '@v2/domains/routing/types/routing.types'
import { getRoutePath } from '@v2/domains/routing/utils/route.utils'

import {
  CreateClinicDto,
  CreateDoctorClinicDto,
  UpdateClinicDto,
} from 'src/api/generated'

import {
  useCreateClinicDoctorMutation,
  useCreateClinicMutation,
  useDeleteClinicMutation,
  useUpdateClinicMutation,
} from '../api/clinic.api'

export const useClinicsMutations = () => {
  const router = useRouter()

  const [createClinicMutation, createClinicStatus] = useCreateClinicMutation()
  const [updateClinicMutation, updateClinicStatus] = useUpdateClinicMutation()
  const [deleteClinicMutation, deleteClinicStatus] = useDeleteClinicMutation()
  const [createClinicDoctorMutation, createClinicDoctorStatus] =
    useCreateClinicDoctorMutation()

  const createClinic = async (
    createClinicDto: CreateClinicDto,
  ): Promise<boolean> => {
    try {
      const organization = await createClinicMutation({
        ...createClinicDto,
      }).unwrap()
      modals.closeAll()
      showModalNotification({
        title: 'Success',
        subtitle: 'Clinic successfully created!',
        buttonText: 'See Clinic Detail',
        path: `${getRoutePath(DEFAULT_ROUTES.CLINICS)}/${organization.id}`,
      })
      return true
    } catch (err) {
      return handleMutationError(err, 'Creating clinic')
    }
  }

  const updateClinic = async (
    updateClinicDto: UpdateClinicDto,
    id: string,
  ): Promise<boolean> => {
    try {
      await updateClinicMutation({
        id,
        ...updateClinicDto,
      }).unwrap()
      modals.closeAll()
      showModalNotification({
        title: 'Success',
        subtitle: 'Clinic successfully updated!',
      })
      return true
    } catch (err) {
      return handleMutationError(err, 'Update clinic')
    }
  }

  const deleteClinic = async (clinicId: string): Promise<boolean> => {
    try {
      await deleteClinicMutation(clinicId).unwrap()
      modals.closeAll()
      showModalNotification({
        title: 'Success',
        subtitle: 'Clinic successfully deleted!',
      })
      router.push(getRoutePath(DEFAULT_ROUTES.LOCATIONS))
      return true
    } catch (err) {
      return handleMutationError(err, 'Delete clinic')
    }
  }

  const createClinicDoctor = async (
    createClinicDoctor: CreateDoctorClinicDto,
  ): Promise<boolean> => {
    try {
      await createClinicDoctorMutation({
        ...createClinicDoctor,
      }).unwrap()
      modals.closeAll()
      showModalNotification({
        title: 'Success',
        subtitle: 'Clinic Provider successfully created!',
      })
      return true
    } catch (err) {
      return handleMutationError(err, 'Creating clinic provider')
    }
  }

  return {
    createClinic,
    isLoadingCreate: createClinicStatus.isLoading,
    updateClinic,
    isLoadingUpdate: updateClinicStatus.isLoading,
    deleteClinic,
    isLoadingDelete: deleteClinicStatus.isLoading,
    createClinicDoctor,
    isLoadingCreateDoctor: createClinicDoctorStatus.isLoading,
  }
}
